import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Calendar, 
  Edit3, 
  Trash2, 
  Eye,
  Plus,
  MapPin,
  Users,
  DollarSign,
  Tag,
  Star,
  Archive,
  FileText,
  MoreHorizontal,
  ChevronDown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/axios.config';
import { baseUrl } from '../../utils/url';

function RecentActivities() {
  const [activities, setActivities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortBy, setSortBy] = useState('activityDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch activities with filters and pagination
  const handleGetActivities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        category: selectedCategory,
        status: selectedStatus,
        sortBy,
        sortOrder
      });

      const { data } = await api.get(`recent-activities/get?${params}`);
      
      if (data.success) {
        setActivities(data.data.activities);
      }
    } catch (error) {
      toast.error('Failed to fetch activities');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const handleGetCategories = async () => {
    try {
      const { data } = await api.get('activity-categories/get');
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Delete activity
  const handleDeleteActivity = async (id) => {
    if (!window.confirm('আপনি কি নিশ্চিত যে এই কার্যক্রমটি মুছে ফেলতে চান?')) {
      return;
    }

    try {
      const { data } = await api.delete(`recent-activities/${id}`);
      
      if (data.success) {
        toast.success('কার্যক্রম সফলভাবে মুছে ফেলা হয়েছে');
        handleGetActivities();
      }
    } catch (error) {
      toast.error('কার্যক্রম মুছতে ব্যর্থ');
    }
  };

  // Update activity status
  const handleUpdateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`recent-activities/${id}`, { status });
      
      if (data.success) {
        toast.success('স্ট্যাটাস আপডেট হয়েছে');
        handleGetActivities();
      }
    } catch (error) {
      toast.error('স্ট্যাটাস আপডেট করতে ব্যর্থ');
    }
  };

  // Toggle featured status
  const handleToggleFeatured = async (id, featured) => {
    try {
      const { data } = await api.patch(`recent-activities/${id}`, { featured: !featured });
      
      if (data.success) {
        toast.success('ফিচার স্ট্যাটাস আপডেট হয়েছে');
        handleGetActivities();
      }
    } catch (error) {
      toast.error('ফিচার স্ট্যাটাস আপডেট করতে ব্যর্থ');
    }
  };

  useEffect(() => {
    handleGetActivities();
  }, [currentPage, searchTerm, selectedCategory, selectedStatus, sortBy, sortOrder]);

  useEffect(() => {
    handleGetCategories();
  }, []);

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('bn-BD', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      published: 'bg-green-100 text-green-800',
      draft: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.published;
  };

  // Get category color
  const getCategoryColor = (category) => {
    return category?.color || '#3B82F6';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">সাম্প্রতিক কার্যক্রমসমূহ</h1>
          <p className="text-gray-600 mt-1">
            মোট {activities.length} টি কার্যক্রম পাওয়া গেছে
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn btn-primary">
            <Plus size={20} />
            নতুন কার্যক্রম যুক্ত করুন
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="কার্যক্রম খুঁজুন..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input input-bordered w-full pl-10"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="select select-bordered w-full"
            >
              <option value="">সকল বিভাগ</option>
              {categories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="select select-bordered w-full"
            >
              <option value="">সকল স্ট্যাটাস</option>
              <option value="published">প্রকাশিত</option>
              <option value="draft">খসড়া</option>
              <option value="archived">সংরক্ষিত</option>
            </select>
          </div>

          {/* Sort */}
          <div>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="select select-bordered w-full"
            >
              <option value="activityDate-desc">নতুন প্রথম</option>
              <option value="activityDate-asc">পুরাতন প্রথম</option>
              <option value="title-asc">নাম অনুসারে (ক-য়)</option>
              <option value="views-desc">বেশি দেখা</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activities.map((activity) => (
          <div key={activity._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300">
            {/* Image */}
            <div className="relative h-48 overflow-hidden">
              <img
                src={`${baseUrl}${activity.imageUrl}`}
                alt={activity.title}
                className="w-full h-full object-cover"
              />
              
              {/* Featured Badge */}
              {activity.featured && (
                <div className="absolute top-3 left-3">
                  <div className="bg-yellow-500 text-white px-2 py-1 rounded-full flex items-center text-xs font-medium">
                    <Star size={12} className="mr-1 fill-current" />
                    বিশেষ
                  </div>
                </div>
              )}

              {/* Status Badge */}
              <div className="absolute top-3 right-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(activity.status)}`}>
                  {activity.status === 'published' ? 'প্রকাশিত' : 
                   activity.status === 'draft' ? 'খসড়া' : 'সংরক্ষিত'}
                </span>
              </div>

              {/* Category */}
              {activity.category && (
                <div className="absolute bottom-3 left-3">
                  <span 
                    className="px-2 py-1 rounded-full text-white text-xs font-medium"
                    style={{ backgroundColor: getCategoryColor(activity.category) }}
                  >
                    {activity.category.name}
                  </span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-5">
              <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                {activity.title}
              </h3>
              
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {activity.shortDescription}
              </p>

              {/* Meta Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-gray-500 text-xs">
                  <Calendar size={14} className="mr-2" />
                  <span>{formatDate(activity.activityDate)}</span>
                </div>
                
                {activity.location && (
                  <div className="flex items-center text-gray-500 text-xs">
                    <MapPin size={14} className="mr-2" />
                    <span>{activity.location}</span>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center text-gray-500">
                    <Eye size={14} className="mr-1" />
                    <span>{activity.views} বার দেখা হয়েছে</span>
                  </div>
                  
                  {activity.participants > 0 && (
                    <div className="flex items-center text-gray-500">
                      <Users size={14} className="mr-1" />
                      <span>{activity.participants}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleFeatured(activity._id, activity.featured)}
                    className={`p-2 rounded-lg transition-colors ${
                      activity.featured 
                        ? 'text-yellow-600 hover:bg-yellow-50' 
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={activity.featured ? 'বিশেষ থেকে সরান' : 'বিশেষ করুন'}
                  >
                    <Star size={16} className={activity.featured ? 'fill-current' : ''} />
                  </button>
                  
                  <Link
                    to={`/dashboard/edit-activity/${activity._id}`}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="সম্পাদনা করুন"
                  >
                    <Edit3 size={16} />
                  </Link>
                  
                  <button
                    onClick={() => handleDeleteActivity(activity._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="মুছে ফেলুন"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Status Dropdown */}
                <div className="dropdown dropdown-end">
                  <label tabIndex={0} className="btn btn-ghost btn-sm">
                    <MoreHorizontal size={16} />
                  </label>
                  <ul tabIndex={0} className="dropdown-content menu p-2 shadow-lg bg-base-100 rounded-lg w-52">
                    <li>
                      <button onClick={() => handleUpdateStatus(activity._id, 'published')}>
                        <FileText size={16} />
                        প্রকাশ করুন
                      </button>
                    </li>
                    <li>
                      <button onClick={() => handleUpdateStatus(activity._id, 'draft')}>
                        <Edit3 size={16} />
                        খসড়া করুন
                      </button>
                    </li>
                    <li>
                      <button onClick={() => handleUpdateStatus(activity._id, 'archived')}>
                        <Archive size={16} />
                        সংরক্ষণ করুন
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {activities.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">কোন কার্যক্রম পাওয়া যায়নি</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedCategory || selectedStatus 
              ? 'আপনার ফিল্টার অনুযায়ী কোন কার্যক্রম পাওয়া যায়নি। অন্য অনুসন্ধান চেষ্টা করুন।'
              : 'এখনো কোন কার্যক্রম যুক্ত করা হয়নি।'
            }
          </p>
          <button className="btn btn-primary">
            <Plus size={20} />
            প্রথম কার্যক্রম যুক্ত করুন
          </button>
        </div>
      )}

      {/* Pagination */}
      {activities.length > 0 && (
        <div className="flex justify-center">
          <div className="join">
            <button 
              className="join-item btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              পূর্ববর্তী
            </button>
            <button className="join-item btn btn-active">
              পৃষ্ঠা {currentPage}
            </button>
            <button 
              className="join-item btn"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={activities.length < itemsPerPage}
            >
              পরবর্তী
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecentActivities;